/**
 * CSV Export Utility — reusable client-side CSV generation and download
 *
 * Features:
 * - Generic typed column definitions
 * - Proper CSV escaping (commas, quotes, newlines)
 * - UTF-8 BOM for Excel compatibility
 * - Browser download trigger via Blob
 */

/**
 * Column definition for CSV export
 */
export interface CsvColumn<T> {
  /** Column header text (will appear in first row) */
  header: string;
  /** Function to extract cell value from a data row */
  accessor: (row: T) => string | number | null | undefined;
}

/**
 * Escape a CSV value — wraps in double-quotes if it contains
 * comma, double-quote, or newline characters.
 */
export function escapeCsvValue(value: string | number | null | undefined): string {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Convert an array of typed objects into a CSV string.
 *
 * @param data - Array of row objects
 * @param columns - Column definitions with headers and accessors
 * @returns CSV-formatted string (header row + data rows)
 */
export function convertToCsv<T>(data: T[], columns: CsvColumn<T>[]): string {
  const headerRow = columns.map((c) => escapeCsvValue(c.header)).join(',');
  const dataRows = data.map((row) =>
    columns.map((col) => escapeCsvValue(col.accessor(row))).join(','),
  );
  return [headerRow, ...dataRows].join('\n');
}

/**
 * Trigger a browser file download with the given CSV content.
 *
 * Prepends UTF-8 BOM (\uFEFF) so Excel correctly detects encoding.
 *
 * @param csvContent - CSV string to download
 * @param filename - Name for the downloaded file (should end in .csv)
 */
export function downloadCsv(csvContent: string, filename: string): void {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
